import React from 'react';
import { reportClientError } from '../services/clientErrorService';
import { getRunningMainBundle, checkBundleFreshness, reloadOntoFreshBundle, STALE } from '../utils/buildVersion';
import './ErrorBoundary.css';

/**
 * The containment half of the 2026-08-21 fix.
 *
 * WHY: React unmounts the ENTIRE tree when a render throws and nothing catches
 * it. With no boundary anywhere in this app, one broken component produced a
 * blank white page across the whole of HMS — no navigation, no message, no way
 * back except a reload that (with a cached bundle) reproduced it exactly.
 *
 * Two jobs, in this order:
 *  1. If this tab is running an out-of-date bundle, heal it — one reload onto
 *     the deployed build. That is the actual cure for the stale-bundle class,
 *     and it happens before the user is shown anything.
 *  2. Otherwise report the crash and render a card IN PLACE, so whatever sits
 *     outside this boundary (navbar, chrome, the rest of the app) stays alive.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, reference: null, healing: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // A stale bundle is a cause, not a crash to report — check it first so we
    // heal rather than filling the crash table with contract-drift noise.
    checkBundleFreshness()
      .then((status) => {
        if (status === STALE && reloadOntoFreshBundle()) {
          this.setState({ healing: true });
          return null;
        }
        return reportClientError({
          name: error && error.name,
          message: error && error.message,
          stack: error && error.stack,
          componentStack: info && info.componentStack,
          source: this.props.boundary || 'react',
          bundle: getRunningMainBundle(),
        });
      })
      .then((reference) => {
        if (reference) this.setState({ reference });
      })
      .catch(() => {
        /* Reporting must never mask the original crash. */
      });
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.healing) {
      return (
        <div className="hms-error-boundary hms-error-boundary--healing" role="status">
          <p>This page was out of date. Updating to the latest version…</p>
        </div>
      );
    }

    const { reference } = this.state;

    return (
      <div className="hms-error-boundary" role="alert">
        <h2 className="hms-error-boundary__title">This screen ran into a problem</h2>
        <p className="hms-error-boundary__body">
          The rest of IMT is still working — you can carry on elsewhere using the menu.
        </p>
        <div className="hms-error-boundary__actions">
          <button type="button" className="hms-error-boundary__button" onClick={this.handleReload}>
            Reload this screen
          </button>
        </div>
        {reference ? (
          <p className="hms-error-boundary__reference">
            Reference <code>{reference}</code> — quote this and we can see exactly what broke.
          </p>
        ) : (
          <p className="hms-error-boundary__reference">
            We could not record the details automatically — please tell us what you were doing.
          </p>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
